import { motion } from "framer-motion";
import { Building2, Calendar, PartyPopper, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { useNavigate } from "react-router-dom";

export const ServicesSection = () => {
  const { t, getLocalizedPath } = useLanguage();
  const navigate = useNavigate();

  const services = [
    {
      icon: Building2,
      title: t.services.purchase.title,
      subtitle: t.services.purchase.subtitle,
      description: t.services.purchase.description,
      features: t.services.purchase.features,
      cta: t.services.purchase.cta,
      ctaVariant: "default" as const,
      accent: "primary",
      action: "contact",
    },
    {
      icon: Calendar,
      title: t.services.longRental.title,
      subtitle: t.services.longRental.subtitle,
      description: t.services.longRental.description,
      features: t.services.longRental.features,
      cta: t.services.longRental.cta,
      ctaVariant: "outline" as const,
      accent: "secondary",
      action: "contact",
    },
    {
      icon: PartyPopper,
      title: t.services.shortRental.title,
      subtitle: t.services.shortRental.subtitle,
      description: t.services.shortRental.description,
      features: t.services.shortRental.features,
      cta: t.services.shortRental.cta,
      ctaVariant: "outline" as const,
      accent: "secondary",
      action: "rental",
    },
  ];

  const handleAction = (action: string) => {
    if (action === "rental") {
      navigate(getLocalizedPath("/rental"));
    } else {
      navigate(getLocalizedPath("/contact"));
    }
  };

  return (
    <section className="py-32 lg:py-40 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
            {t.services.title} <span className="text-primary">{t.services.titleHighlight}</span>
          </h2>
        </motion.div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <div className="h-full bg-card rounded-3xl p-8 lg:p-10 border border-border hover:border-primary/30 transition-all duration-500 hover:shadow-xl flex flex-col">
                {/* Icon & Title */}
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className={`w-14 h-14 rounded-2xl ${
                      service.accent === "primary" ? "bg-primary/10" : "bg-secondary/10"
                    } flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                  >
                    <service.icon
                      className={`w-7 h-7 ${service.accent === "primary" ? "text-primary" : "text-secondary"}`}
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">{service.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{service.subtitle}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-muted-foreground font-light text-base mb-6 leading-relaxed">{service.description}</p>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-secondary" />
                      </div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  variant={service.ctaVariant}
                  onClick={() => handleAction(service.action)}
                  size="lg"
                  className={`w-full py-5 text-sm group/btn ${
                    service.ctaVariant === "default"
                      ? "bg-primary hover:bg-primary/90 shadow-glow-blue"
                      : "border-border hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  {service.cta}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
